import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { InGameCurrency } from '@sorare/core/src/__generated__/globalTypes';
import fiat from '@sorare/core/src/assets/icons/fiat.png';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import { toSnakeCase } from '@sorare/core/src/lib/toSnakeCase';

import type { getRewardImage_anyRewardConfigInterface } from './__generated__/rewards.graphql';

export const getRewardImage = withFragments(
  (anyRewardConfig: getRewardImage_anyRewardConfigInterface) => {
    if (isType(anyRewardConfig, 'MonetaryRewardConfig')) {
      return fiat;
    }
    if (isType(anyRewardConfig, 'CardShardRewardConfig')) {
      const { rarity } = anyRewardConfig;
      return `${FRONTEND_ASSET_HOST}/football/shards/${rarity}.png`;
    }
    if (isType(anyRewardConfig, 'ShopItemRewardConfig')) {
      return anyRewardConfig.shopItem.pictureUrl;
    }
    if (isType(anyRewardConfig, 'ExperienceRewardConfig')) {
      return anyRewardConfig.pictureUrl || '';
    }
    if (isType(anyRewardConfig, 'InGameCurrencyRewardConfig')) {
      switch (anyRewardConfig.currency) {
        case InGameCurrency.OLD_CLUB_SHOP_COIN:
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/coin.png`;
        case InGameCurrency.COMMON_XP:
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-common.png`;
        case InGameCurrency.LIMITED_XP:
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-limited.png`;
        case InGameCurrency.RARE_XP:
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-rare.png`;
        case InGameCurrency.SUPER_RARE_XP:
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-super_rare.png`;
        case InGameCurrency.UNIQUE_XP:
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-unique.png`;
        default:
          return '';
      }
    }
    if (isType(anyRewardConfig, 'CardPacksRewardConfig')) {
      return anyRewardConfig.cardPack?.pictureUrl || '';
    }
    if (isType(anyRewardConfig, 'CoinRewardConfig')) {
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/coin.png`;
    }
    if (isType(anyRewardConfig, 'ConversionCreditRewardConfig')) {
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/conversion-credit.png`;
    }
    if (isType(anyRewardConfig, 'ProbabilisticBundleRewardConfig')) {
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/box-${toSnakeCase(anyRewardConfig.probabilisticBundleConfig.rarity)}.png`;
    }
    if (isType(anyRewardConfig, 'DeliverableItemRewardConfig')) {
      switch (anyRewardConfig.deliverableItem.__typename) {
        case 'ExperienceDeliverableItem':
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/experience.png`;
        case 'JerseyDeliverableItem':
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/jersey.png`;
        case 'TicketsDeliverableItem':
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/tickets.png`;
        case 'WearableDeliverableItem':
          return `${FRONTEND_ASSET_HOST}/football/rewards/icons/merch.png`;
        default:
          return '';
      }
    }
    return '';
  },
  {
    anyRewardConfigInterface: gql`
      fragment getRewardImage_anyRewardConfigInterface on AnyRewardConfigInterface {
        id
        ... on CardShardRewardConfig {
          id
          rarity
        }
        ... on ShopItemRewardConfig {
          id
          shopItem {
            id
            pictureUrl
          }
        }
        ... on ExperienceRewardConfig {
          id
          pictureUrl
        }
        ... on InGameCurrencyRewardConfig {
          id
          currency
        }
        ... on CardPacksRewardConfig {
          id
          cardPack {
            slug
            pictureUrl
          }
        }
        ... on ProbabilisticBundleRewardConfig {
          id
          probabilisticBundleConfig {
            id
            rarity
          }
        }
        ... on DeliverableItemRewardConfig {
          id
          deliverableItem {
            slug
          }
        }
      }
    ` as TypedDocumentNode<getRewardImage_anyRewardConfigInterface>,
  }
);
